import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import API_BASE_URL from '../config';

/** Data shape for an existing auction passed in for editing. */
export interface AuctionEditData {
  id: number;
  status: string; // draft | scheduled | active
  starts_at: string;
  ends_at: string;
  minimum_bid: number; // cents
  reserve_price: number | null; // cents
  reserve_visible: boolean;
  buy_now_price: number | null; // cents
  quantity: number;
  collection_window_days: number;
  collection_notes: string | null;
  auction_group: string | null;
}

interface AuctionSetupModalProps {
  itemId: number;
  itemName: string;
  token: string;
  onClose: () => void;
  onCreated: () => void;
  /** If provided, modal opens in edit mode with these values pre-filled. */
  editData?: AuctionEditData;
  /** Item's sale_price in cents — used as default Buy Now price for new auctions. */
  itemSalePrice?: number | null;
}

const formatDateTimeLocal = (d: Date) => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const AuctionSetupModal: React.FC<AuctionSetupModalProps> = ({
  itemId,
  itemName,
  token,
  onClose,
  onCreated,
  editData,
  itemSalePrice,
}) => {
  const isEdit = !!editData;
  const isPublished = isEdit && (editData!.status === 'scheduled' || editData!.status === 'active');
  const isActive = isEdit && editData!.status === 'active';

  // --- Derive initial values ---
  const initStartsAt = editData
    ? formatDateTimeLocal(new Date(editData.starts_at))
    : formatDateTimeLocal(new Date());

  const initDurationDays = editData
    ? String(Math.max(1, Math.round((new Date(editData.ends_at).getTime() - new Date(editData.starts_at).getTime()) / (1000 * 60 * 60 * 24))))
    : '7';

  const [startsAt, setStartsAt] = useState(initStartsAt);
  const [durationDays, setDurationDays] = useState(initDurationDays);
  const [minimumBid, setMinimumBid] = useState(editData ? (editData.minimum_bid / 100).toFixed(2) : '1.00');
  const [reservePrice, setReservePrice] = useState(editData?.reserve_price ? (editData.reserve_price / 100).toFixed(2) : '');
  const [reserveVisible, setReserveVisible] = useState(editData?.reserve_visible ?? false);
  const [buyNowPrice, setBuyNowPrice] = useState(
    editData?.buy_now_price ? (editData.buy_now_price / 100).toFixed(2)
    : (itemSalePrice ? (itemSalePrice / 100).toFixed(2) : '')
  );
  const [quantity, setQuantity] = useState(editData ? String(editData.quantity) : '1');
  const [collectionNotes, setCollectionNotes] = useState(editData?.collection_notes ?? '');
  const [collectionWindowDays, setCollectionWindowDays] = useState(editData ? String(editData.collection_window_days) : '7');
  const [auctionGroup, setAuctionGroup] = useState(editData?.auction_group ?? '');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Original values for constraint hints
  const origDurationDays = editData ? parseInt(initDurationDays, 10) : null;
  const origMinBidCents = editData?.minimum_bid ?? null;
  const origBuyNowCents = editData?.buy_now_price ?? null;
  const origWindowDays = editData?.collection_window_days ?? null;

  const handleSubmit = async () => {
    setError(null);

    const minBidCents = Math.round(parseFloat(minimumBid) * 100);
    if (isNaN(minBidCents) || minBidCents < 1) {
      setError('Minimum bid must be at least $0.01');
      return;
    }

    const reserveCents = reservePrice ? Math.round(parseFloat(reservePrice) * 100) : null;
    if (reservePrice && (isNaN(reserveCents!) || reserveCents! < 1)) {
      setError('Reserve price must be at least $0.01');
      return;
    }

    const buyNowCents = buyNowPrice ? Math.round(parseFloat(buyNowPrice) * 100) : null;
    if (buyNowPrice && (isNaN(buyNowCents!) || buyNowCents! < 1)) {
      setError('Buy It Now price must be at least $0.01');
      return;
    }

    const qty = parseInt(quantity, 10);
    if (isNaN(qty) || qty < 1) {
      setError('Quantity must be at least 1');
      return;
    }

    const windowDays = parseInt(collectionWindowDays, 10);
    if (isNaN(windowDays) || windowDays < 1) {
      setError('Collection window must be at least 1 day');
      return;
    }

    const duration = parseInt(durationDays, 10);
    if (isNaN(duration) || duration < 1) {
      setError('Duration must be at least 1 day');
      return;
    }

    if (!startsAt) {
      setError('Start time is required');
      return;
    }

    // Client-side constraint checks for published auctions
    if (isPublished) {
      if (origDurationDays !== null && duration > origDurationDays) {
        setError(`Days can only be shortened (was ${origDurationDays})`);
        return;
      }
      if (origMinBidCents !== null && minBidCents > origMinBidCents) {
        setError(`Minimum bid can only be lowered (was $${(origMinBidCents / 100).toFixed(2)})`);
        return;
      }
      if (origBuyNowCents !== null) {
        if (buyNowCents === null) {
          setError('Buy It Now price cannot be removed');
          return;
        }
        if (buyNowCents > origBuyNowCents) {
          setError(`Buy It Now can only be lowered (was $${(origBuyNowCents / 100).toFixed(2)})`);
          return;
        }
      }
      if (origWindowDays !== null && windowDays < origWindowDays) {
        setError(`Pickup window can only be extended (was ${origWindowDays} days)`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const startDate = new Date(startsAt);
      const endsAt = new Date(startDate.getTime() + duration * 24 * 60 * 60 * 1000);

      if (isEdit) {
        // PATCH existing auction
        const body: any = {
          starts_at: startDate.toISOString(),
          ends_at: endsAt.toISOString(),
          minimum_bid: minBidCents,
          collection_window_days: windowDays,
          collection_notes: collectionNotes.trim() || null,
          auction_group: auctionGroup.trim() || null,
        };
        // Only send buy_now_price if changed or newly added
        if (buyNowCents !== null) body.buy_now_price = buyNowCents;
        else if (!isPublished) body.buy_now_price = null; // Draft: allow clearing

        // Draft: send all fields; published: backend enforces constraints
        if (!isPublished) {
          body.quantity = qty;
          if (reserveCents) {
            body.reserve_price = reserveCents;
            body.reserve_visible = reserveVisible;
          } else {
            body.reserve_price = null;
            body.reserve_visible = false;
          }
        }

        const response = await fetch(`${API_BASE_URL}/api/auction-items/${editData!.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });

        const data = await response.json();
        if (!response.ok) {
          setError(data.error || 'Failed to update auction');
          return;
        }

        onCreated();
      } else {
        // POST new auction
        const body: any = {
          item_id: itemId,
          starts_at: startDate.toISOString(),
          ends_at: endsAt.toISOString(),
          minimum_bid: minBidCents,
          quantity: qty,
          collection_window_days: windowDays,
        };

        if (reserveCents) {
          body.reserve_price = reserveCents;
          body.reserve_visible = reserveVisible;
        }
        if (buyNowCents) body.buy_now_price = buyNowCents;
        if (collectionNotes.trim()) body.collection_notes = collectionNotes.trim();
        if (auctionGroup.trim()) body.auction_group = auctionGroup.trim();

        const response = await fetch(`${API_BASE_URL}/api/auction-items`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });

        const data = await response.json();
        if (!response.ok) {
          setError(data.error || 'Failed to create auction');
          return;
        }

        // Auto-publish (set to scheduled or active)
        await fetch(`${API_BASE_URL}/api/auction-items/${data.id}/publish`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });

        onCreated();
      }
    } catch {
      setError(isEdit ? 'Failed to update auction. Please try again.' : 'Failed to create auction. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const hintStyle: React.CSSProperties = {
    fontSize: '11px',
    color: 'var(--color-text-muted)',
    marginTop: 2,
    fontStyle: 'italic',
  };

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg-primary)',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          width: '100%',
          maxWidth: 480,
          maxHeight: '85vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          padding: '0 16px 16px',
          paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
          boxSizing: 'border-box',
        }}
      >
        {/* Handle bar */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0 4px 0' }}>
          <div style={{ width: 36, height: 5, backgroundColor: 'var(--color-border-strong)', borderRadius: 3 }} />
        </div>

        <div style={{ position: 'relative', margin: '8px 0 16px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, textAlign: 'center', color: 'var(--color-text-primary)', margin: 0 }}>
            {isEdit ? `Edit: ${itemName}` : `Auction: ${itemName}`}
          </h2>
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: -2, right: 0,
              width: 32, height: 32, borderRadius: 16,
              border: 'none', background: 'var(--color-bg-secondary)',
              color: 'var(--color-text-muted)', fontSize: 18,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', lineHeight: 1,
            }}
            aria-label="Close"
          >✕</button>
        </div>

        {isPublished && (
          <div style={{ padding: '8px 12px', background: '#eff6ff', color: '#1e40af', borderRadius: 8, fontSize: '13px', marginBottom: 12 }}>
            Some fields are restricted because this auction is {editData!.status}.
          </div>
        )}

        {error && (
          <div style={{ padding: '10px 12px', background: '#fef2f2', color: '#991b1b', borderRadius: 8, fontSize: '14px', marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Start time + Duration */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10 }}>
            <label style={labelStyle}>
              <span style={labelTextStyle}>Starts</span>
              <input
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                style={{ ...inputStyle, ...(isActive ? disabledInputStyle : {}) }}
                disabled={isActive}
              />
              {isActive && <span style={hintStyle}>Locked (auction is active)</span>}
            </label>
            <label style={labelStyle}>
              <span style={labelTextStyle}>Days</span>
              <input
                type="number"
                value={durationDays}
                onChange={(e) => setDurationDays(e.target.value)}
                min="1"
                max={isPublished && origDurationDays ? origDurationDays : 90}
                style={{ ...inputStyle, width: 64 }}
              />
              {isPublished && <span style={hintStyle}>≤ {origDurationDays}d</span>}
            </label>
          </div>

          {/* Pricing */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={labelStyle}>
              <span style={labelTextStyle}>Minimum Bid ($)</span>
              <input
                type="number"
                value={minimumBid}
                onChange={(e) => setMinimumBid(e.target.value)}
                step="0.01"
                min="0.01"
                max={isPublished && origMinBidCents ? (origMinBidCents / 100) : undefined}
                style={inputStyle}
              />
              {isPublished && origMinBidCents && <span style={hintStyle}>≤ ${(origMinBidCents / 100).toFixed(2)}</span>}
            </label>
            <label style={labelStyle}>
              <span style={labelTextStyle}>Buy It Now ($)</span>
              <input
                type="number"
                value={buyNowPrice}
                onChange={(e) => setBuyNowPrice(e.target.value)}
                step="0.01"
                min="0.01"
                max={isPublished && origBuyNowCents ? (origBuyNowCents / 100) : undefined}
                placeholder="Optional"
                style={inputStyle}
              />
              {isPublished && origBuyNowCents && <span style={hintStyle}>≤ ${(origBuyNowCents / 100).toFixed(2)}</span>}
              {isPublished && !origBuyNowCents && <span style={hintStyle}>Can add</span>}
            </label>
          </div>

          {/* Reserve */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, alignItems: 'end' }}>
            <label style={labelStyle}>
              <span style={labelTextStyle}>Reserve Price ($)</span>
              <input
                type="number"
                value={reservePrice}
                onChange={(e) => setReservePrice(e.target.value)}
                step="0.01"
                min="0.01"
                placeholder="Optional"
                style={{ ...inputStyle, ...(isPublished ? disabledInputStyle : {}) }}
                disabled={isPublished}
              />
              {isPublished && <span style={hintStyle}>Locked</span>}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0', cursor: isPublished ? 'default' : 'pointer', opacity: isPublished ? 0.5 : 1 }}>
              <input type="checkbox" checked={reserveVisible} onChange={(e) => setReserveVisible(e.target.checked)} disabled={isPublished} />
              <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>Show to bidders</span>
            </label>
          </div>

          {/* Quantity + Collection Window */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={labelStyle}>
              <span style={labelTextStyle}>Quantity (lot size)</span>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                min="1"
                style={{ ...inputStyle, ...(isPublished ? disabledInputStyle : {}) }}
                disabled={isPublished}
              />
              {isPublished && <span style={hintStyle}>Locked</span>}
            </label>
            <label style={labelStyle}>
              <span style={labelTextStyle}>Pickup window (days)</span>
              <input
                type="number"
                value={collectionWindowDays}
                onChange={(e) => setCollectionWindowDays(e.target.value)}
                min={isPublished && origWindowDays ? origWindowDays : 1}
                style={inputStyle}
              />
              {isPublished && <span style={hintStyle}>≥ {origWindowDays}d</span>}
            </label>
          </div>

          {/* Collection notes */}
          <label style={labelStyle}>
            <span style={labelTextStyle}>Pickup instructions</span>
            <textarea
              value={collectionNotes}
              onChange={(e) => setCollectionNotes(e.target.value)}
              placeholder="e.g. Local pickup in Portland, OR"
              rows={2}
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </label>

          {/* Auction group */}
          <label style={labelStyle}>
            <span style={labelTextStyle}>Auction group (optional)</span>
            <input type="text" value={auctionGroup} onChange={(e) => setAuctionGroup(e.target.value)} placeholder='e.g. "Spring Clearance"' style={inputStyle} />
          </label>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button
              onClick={onClose}
              disabled={submitting}
              style={{
                flex: 1,
                padding: '12px',
                background: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                fontSize: '15px',
                color: 'var(--color-text-primary)',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                flex: 2,
                padding: '12px',
                background: 'var(--color-accent)',
                border: 'none',
                borderRadius: 8,
                fontSize: '15px',
                fontWeight: 600,
                color: '#fff',
                cursor: 'pointer',
                opacity: submitting ? 0.6 : 1,
              }}
            >
              {submitting
                ? (isEdit ? 'Saving...' : 'Creating...')
                : (isEdit ? 'Save Changes' : 'Create & Publish Auction')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const labelTextStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 500,
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const inputStyle: React.CSSProperties = {
  padding: '10px 12px',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  fontSize: '15px',
  background: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  boxSizing: 'border-box',
  width: '100%',
  minWidth: 0,
};

const disabledInputStyle: React.CSSProperties = {
  background: 'var(--color-bg-tertiary)',
  color: 'var(--color-text-muted)',
  cursor: 'not-allowed',
};

export default AuctionSetupModal;
